import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import "./Lightbox.css";

/** Visionneuse plein écran : navigation clavier, zoom (lecture des plans), pan par glisser / défilement. */
const Lightbox = ({ items, index = 0, onClose }) => {
  const [current, setCurrent] = useState(index);
  const [zoomed, setZoomed] = useState(false);
  const [zoomWidth, setZoomWidth] = useState(null);
  const closeRef = useRef(null);
  const scrollRef = useRef(null);
  const imgRef = useRef(null);
  const drag = useRef(null);
  const item = items[current];

  const go = useCallback(
    (delta) => {
      setZoomed(false);
      setCurrent((i) => (i + delta + items.length) % items.length);
    },
    [items.length]
  );

  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && items.length > 1) go(1);
      else if (e.key === "ArrowLeft" && items.length > 1) go(-1);
      else if (e.key === "+" || e.key === "=") setZoomed(true);
      else if (e.key === "-") setZoomed(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previous?.focus?.();
    };
  }, [go, items.length, onClose]);

  const toggleZoom = () => {
    const natural = imgRef.current?.naturalWidth || 1200;
    setZoomWidth(Math.max(window.innerWidth, Math.round(natural * 2.5)));
    setZoomed((z) => !z);
  };

  useEffect(() => {
    if (zoomed && scrollRef.current) {
      const el = scrollRef.current;
      el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
      el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
    }
  }, [zoomed]);

  const onPointerDown = (e) => {
    if (!zoomed) return;
    drag.current = { x: e.clientX, y: e.clientY, left: scrollRef.current.scrollLeft, top: scrollRef.current.scrollTop, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) drag.current.moved = true;
    scrollRef.current.scrollLeft = drag.current.left - dx;
    scrollRef.current.scrollTop = drag.current.top - dy;
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    const moved = drag.current.moved;
    drag.current = null;
    if (!moved) toggleZoom();
  };

  return (
    <div className="lb" role="dialog" aria-modal="true" aria-label={item.label || item.alt} data-testid="lightbox">
      <div className="lb-bar">
        <div className="lb-bar-text">
          {item.label && <span className="lb-label">{item.label}</span>}
          {items.length > 1 && (
            <span className="lb-counter" data-testid="lightbox-counter">
              {current + 1} / {items.length}
            </span>
          )}
        </div>
        <div className="lb-actions">
          <button type="button" className="lb-btn" onClick={toggleZoom} aria-pressed={zoomed} aria-label={zoomed ? "Réduire" : "Agrandir"} data-testid="lightbox-zoom">
            {zoomed ? <ZoomOut size={18} /> : <ZoomIn size={18} />}
          </button>
          <button type="button" className="lb-btn" ref={closeRef} onClick={onClose} aria-label="Fermer la visionneuse" data-testid="lightbox-close">
            <X size={20} />
          </button>
        </div>
      </div>

      <div
        className={`lb-stage ${zoomed ? "is-zoomed" : ""}`}
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => { drag.current = null; }}
      >
        <img
          ref={imgRef}
          className="lb-img"
          src={item.src}
          alt={item.alt}
          style={zoomed && zoomWidth ? { width: `${zoomWidth}px` } : undefined}
          onClick={zoomed ? undefined : toggleZoom}
          draggable="false"
          data-testid="lightbox-image"
        />
      </div>

      {items.length > 1 && (
        <>
          <button type="button" className="lb-nav lb-nav-prev" onClick={() => go(-1)} aria-label="Visuel précédent" data-testid="lightbox-prev">
            <ChevronLeft size={24} />
          </button>
          <button type="button" className="lb-nav lb-nav-next" onClick={() => go(1)} aria-label="Visuel suivant" data-testid="lightbox-next">
            <ChevronRight size={24} />
          </button>
        </>
      )}

      <div className="lb-foot">
        {item.caption && <p className="lb-caption">{item.caption}</p>}
        {item.note && <p className="lb-note" data-testid="lightbox-note">{item.note}</p>}
        <p className="lb-hint">{zoomed ? "Glissez pour vous déplacer · clic pour réduire" : "Clic ou + pour agrandir · Échap pour fermer"}</p>
      </div>
    </div>
  );
};

export default Lightbox;
